import { type ComponentType, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress, Grid, styled } from '@mui/material'
import type { Question } from '@pbt/pbt-ui-components'

import PuiPopup, { type PuiPopupProps } from '~/components/common/PuiPopup'
import Typography from '~/components/elements/Typography/Typography'
import FeatureToggle from '~/constants/featureToggle'
import { fetchPreviewQuestion } from '~/store/actions/questions'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getPreviewQuestion,
  getQuestionsIsPreviewing,
} from '~/store/reducers/questions'

import QuestionPreview from './QuestionPreview'

const StyledSpinner = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 200,
})

interface QuestionPreviewContainerProps
  extends Omit<PuiPopupProps, 'children'> {
  Component: ComponentType<any>
  question: Question
}

const QuestionPreviewContainer = ({
  question,
  open,
  ...rest
}: QuestionPreviewContainerProps) => {
  const { t } = useTranslation('Admin')
  const dispatch = useDispatch()
  const isPreviewing = useSelector(getQuestionsIsPreviewing)
  const previewQuestion = useSelector(getPreviewQuestion)
  const isQuestionsDynamicTextAndSubtextEnabled = useSelector(
    getFeatureToggle(FeatureToggle.QUESTIONS_DYNAMIC_TEXT_AND_SUBTEXT),
  )

  useEffect(() => {
    if (open && question && isQuestionsDynamicTextAndSubtextEnabled) {
      dispatch(fetchPreviewQuestion(question))
    }
  }, [open, question, isQuestionsDynamicTextAndSubtextEnabled])

  const renderQuestionContent = () => {
    if (!isQuestionsDynamicTextAndSubtextEnabled) {
      return <QuestionPreview question={question} />
    }

    if (isPreviewing) {
      return (
        <StyledSpinner>
          <CircularProgress size={32} />
        </StyledSpinner>
      )
    }

    return (
      <QuestionPreview
        question={
          previewQuestion
            ? {
                answerInputTypeId: question.answerInputTypeId,
                answerInputSubtypeId: question.answerInputSubtypeId,
                ...previewQuestion,
              }
            : question
        }
      />
    )
  }

  return (
    <PuiPopup open={open} {...rest}>
      <Grid container direction="column" p={3} width={600}>
        <Grid item>
          <Typography.H1 color="brandSecondary">
            {t('Admin:CATALOG.PREVIEW_QUESTION_POPUP.HEADER')}
          </Typography.H1>
        </Grid>
        {renderQuestionContent()}
      </Grid>
    </PuiPopup>
  )
}

export default QuestionPreviewContainer
